import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Package,
  Truck,
  Shield,
  Clock,
  MapPin,
  Phone,
  Mail,
  ChevronRight,
  Search,
  Menu,
  X,
  CheckCircle2,
  Star,
  HelpCircle,
  ChevronDown,
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  Eye,
  Target,
  Maximize2,
  Image,
  Globe,
  ShieldCheck,
} from "lucide-react";
import companyLogo from "../../assets/logo.jpg";
import team1 from "../../assets/team1.png";
import team2 from "../../assets/team2.png";
import team3 from "../../assets/team3.png";
import team4 from "../../assets/team4.png";
import aboutBanner from "../../assets/about-banner.jpeg";
import gallery1 from "../../assets/gallery1.png";
import gallery2 from "../../assets/gallery2.png";
import gallery3 from "../../assets/gallery3.png";
import gallery4 from "../../assets/gallery4.png";
import gallery5 from "../../assets/gallery5.png";
import gallery6 from "../../assets/gallery6.png";
import client1 from "../../assets/client1.png";
import client2 from "../../assets/client2.png";
import { toast } from "react-toastify";
import { createPickUpService } from "../../services/pickUpService";

// --- Components ---
const LoginModal = ({ isOpen, onClose, onLogin }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);

    // Mock authentication delay
    setTimeout(() => {
      const mockUser = {
        name: formData.name || formData.email.split("@")[0] || "Guest User",
        email: formData.email,
        avatar: `https://i.pravatar.cc/150?u=${formData.email}`,
      };
      onLogin(mockUser);
      setIsLoading(false);
      onClose();
    }, 1500);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative w-full max-w-md bg-white rounded-[2.5rem] shadow-2xl overflow-hidden border border-gray-100"
          >
            <div className="p-8 sm:p-10">
              <div className="flex justify-between items-center mb-8">
                <div>
                  <h2 className="text-3xl font-black text-gray-900 leading-tight">
                    {isLogin ? "Welcome Back" : "Join OPS"}
                  </h2>
                  <p className="text-gray-500 font-medium mt-1">
                    {isLogin
                      ? "Sign in to manage your parcels"
                      : "Create an account to get started"}
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400"
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                {!isLogin && (
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-gray-700 ml-1">
                      Full Name
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        required
                        placeholder="John Doe"
                        className="w-full px-6 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:bg-white outline-none transition-all font-medium"
                        value={formData.name}
                        onChange={(e) =>
                          setFormData({ ...formData, name: e.target.value })
                        }
                      />
                    </div>
                  </div>
                )}
                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700 ml-1">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail
                      className="absolute left-6 top-1/2 -translate-y-1/2 text-gray-400"
                      size={20}
                    />
                    <input
                      type="email"
                      required
                      placeholder="name@example.com"
                      className="w-full pl-14 pr-6 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:bg-white outline-none transition-all font-medium"
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700 ml-1">
                    Password
                  </label>
                  <div className="relative">
                    <Shield
                      className="absolute left-6 top-1/2 -translate-y-1/2 text-gray-400"
                      size={20}
                    />
                    <input
                      type="password"
                      required
                      placeholder="••••••••"
                      className="w-full pl-14 pr-6 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:bg-white outline-none transition-all font-medium"
                      value={formData.password}
                      onChange={(e) =>
                        setFormData({ ...formData, password: e.target.value })
                      }
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-4 bg-[#004a99] text-white rounded-2xl font-bold text-lg shadow-xl shadow-blue-900/20 hover:bg-[#003d80] transition-all active:scale-[0.98] disabled:opacity-70 flex items-center justify-center gap-3"
                >
                  {isLoading ? (
                    <div className="w-6 h-6 border-3 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : isLogin ? (
                    "Sign In"
                  ) : (
                    "Create Account"
                  )}
                </button>
              </form>

              <div className="mt-8 text-center">
                <button
                  onClick={() => setIsLogin(!isLogin)}
                  className="text-gray-500 font-bold hover:text-[#004a99] transition-colors"
                >
                  {isLogin
                    ? "Don't have an account? Sign Up"
                    : "Already have an account? Sign In"}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

const Navbar = ({ user, onLoginClick, onLogout }) => {
  const [isOpen, setIsOpen] = useState(false);

  const navLinks = [
    { name: "Home", href: "#home" },
    { name: "Services", href: "#services" },
    { name: "Why OPS", href: "#why-us" },
    { name: "Track", href: "#tracking" },
    // { name: "FAQ", href: "#faq" },
  ];

  return (
    <nav
      className={`fixed top-0 w-full z-50 transition-all duration-300 bg-white/90 backdrop-blur-sm shadow-sm py-3`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          <div className="flex items-center ">
            <a className="flex items-center gap-3 cursor-pointer" href="#home">
              <img
                src={companyLogo}
                alt="OPS Logo"
                className="h-10 w-10 sm:h-12 sm:w-12 object-contain rounded-lg"
                referrerPolicy="no-referrer"
              />
              <div className="flex flex-col">
                <span className="text-lg sm:text-xl font-bold text-[#004a99] leading-tight">
                  Odisha Parcel Service
                </span>
                <span className="text-[8px] sm:text-[10px] text-gray-500 uppercase tracking-widest font-medium">
                  Your Cargo Our Priority
                </span>
              </div>
            </a>
          </div>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center space-x-6">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="text-gray-600 hover:text-[#004a99] font-medium transition-colors"
              >
                {link.name}
              </a>
            ))}
            <div className="space-x-4 flex items-center">
              {/* <div>
                {user ? (
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-3 bg-blue-50 px-4 py-2 rounded-full border border-blue-100">
                      <img
                        src={user.avatar}
                        alt={user.name}
                        className="w-8 h-8 rounded-full border-2 border-white shadow-sm"
                      />
                      <span className="text-sm font-bold text-gray-700">
                        {user.name.split(" ")[0]}
                      </span>
                    </div>
                    <button
                      onClick={onLogout}
                      className="text-gray-400 hover:text-red-500 transition-colors p-2"
                      title="Logout"
                    >
                      <X size={20} />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={onLoginClick}
                    className="border border-[#004a99] text-[#004a99] px-6 py-2 rounded-2xl font-bold hover:bg-[#004a99] hover:text-white transition-all"
                  >
                    Sign In
                  </button>
                )}
              </div> */}
              <button
                // onClick={() => (window.location.hash = "#pickup")}
                className="border border-[#004a99] px-6 py-2 rounded-2xl font-bold bg-[#004a99] text-white transition-all"
              >
                <a href="#pickup">Book Pickup</a>
              </button>
            </div>
          </div>

          {/* Mobile Toggle */}
          <button
            className="md:hidden p-2 text-gray-600 hover:text-[#004a99]"
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white border-b border-gray-100 overflow-hidden"
          >
            <div className="px-4 pt-2 pb-6 space-y-2">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={() => setIsOpen(false)}
                  className="block px-4 py-3 text-base font-medium text-gray-600 hover:text-[#004a99] hover:bg-blue-50 rounded-xl transition-all"
                >
                  {link.name}
                </a>
              ))}
              {/* <div className="px-4">
                {user ? (
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-3 bg-blue-50 px-4 py-2 rounded-full border border-blue-100">
                      <img
                        src={user.avatar}
                        alt={user.name}
                        className="w-8 h-8 rounded-full border-2 border-white shadow-sm"
                      />
                      <span className="text-sm font-bold text-gray-700">
                        {user.name.split(" ")[0]}
                      </span>
                    </div>
                    <button
                      onClick={onLogout}
                      className="text-gray-400 hover:text-red-500 transition-colors p-2"
                      title="Logout"
                    >
                      <X size={20} />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={onLoginClick}
                    className="block w-full text-center border border-[#004a99] text-[#004a99] hover:bg-[#004a99] hover:text-white py-4 rounded-xl font-bold shadow-lg"
                  >
                    Sign In
                  </button>
                )}
              </div> */}
              <div className="pt-2 px-4">
                <a
                  href="#pickup"
                  onClick={() => setIsOpen(false)}
                  className="block w-full text-center bg-[#004a99] text-white py-4 rounded-xl font-bold shadow-lg"
                >
                  Book Pickup Now
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

const WhyUs = () => {
  const stats = [
    {
      label: "Cities Covered",
      value: "30+",
      icon: <MapPin className="text-blue-500" />,
    },
    {
      label: "Happy Customers",
      value: "10k+",
      icon: <Star className="text-yellow-500" />,
    },
    {
      label: "Parcels Delivered",
      value: "50k+",
      icon: <Package className="text-green-500" />,
    },
    {
      label: "Support",
      value: "24/7",
      icon: <Clock className="text-red-500" />,
    },
  ];

  //   const features = [
  //     "Real-time GPS tracking for all shipments",
  //     "Insurance coverage for high-value items",
  //     "Door-to-door pickup and delivery service",
  //     "Specialized handling for fragile goods",
  //     "Competitive and transparent pricing",
  //     "Dedicated customer support team",
  //   ];

  return (
    <section
      id="why-us"
      className="py-24 overflow-x-hidden bg-gradient-to-b from-white to-blue-50/30"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <h2 className="text-4xl font-bold text-gray-900 mb-6 uppercase">
              About Us
            </h2>
            <p className="text-lg text-gray-600 mb-8 text-justify leading-relaxed">
              Odisha Parcel Service Private Limited is proud to serve the
              logistics needs of 30 districts across Odisha, including district
              headquarters and major cities. Our company is committed to
              providing efficient and reliable transportation and warehousing
              solutions to our clients.
              <br />
              <br /> We specialize in both LTL (Less than Truck Load) and FTL
              (Full Truck Load) services, catering to a variety of shipping
              requirements. Whether you need to transport smaller shipments or
              require a full truckload for larger consignments, we ensure timely
              and secure delivery across all districts in Odisha. <br />
              <br /> Our LTL services offer flexibility and cost-efficiency for
              businesses with smaller loads, while our FTL services are ideal
              for larger volumes, providing a direct and uninterrupted
              transportation route. <br />
              <br /> Additionally, we offer 3PL (Third-Party Logistics)
              warehousing solutions to support our clients' storage and
              distribution needs. Our warehouses are strategically located for
              easy access to major routes, ensuring smooth inventory management
              and fast delivery turnaround. <br />
              <br /> At Odisha Parcel Service Private Limited, our goal is to
              provide superior logistics services that meet the growing demands
              of businesses across Odisha, all while ensuring exceptional
              customer satisfaction and operational excellence.
            </p>
            {/* <div className="grid sm:grid-cols-2 gap-4 mb-10">
              {features.map((f, i) => (
                <div key={i} className="flex items-center gap-3">
                  <CheckCircle2 className="text-green-500 shrink-0" size={20} />
                  <span className="text-gray-700 font-medium">{f}</span>
                </div>
              ))}
            </div> */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
              {stats.map((s, i) => (
                <div
                  key={i}
                  className="text-center p-4 bg-white rounded-2xl shadow-sm border border-gray-100"
                >
                  <div className="flex justify-center mb-2">{s.icon}</div>
                  <div className="text-2xl font-bold text-gray-900">
                    {s.value}
                  </div>
                  <div className="text-xs text-gray-500 font-medium uppercase tracking-wider">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="rounded-3xl overflow-hidden shadow-2xl border-4 border-white"
            >
              <img
                src={aboutBanner}
                alt="Our Fleet"
                className="w-full h-[500px] object-cover"
                referrerPolicy="no-referrer"
              />
            </motion.div>
            <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-2xl shadow-xl border border-gray-100 max-w-xs hidden sm:block">
              <div className="flex items-center gap-4 mb-3">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center text-[#004a99]">
                  <Shield size={24} />
                </div>
                <h4 className="font-bold text-gray-900">100% Safe Delivery</h4>
              </div>
              <p className="text-sm text-gray-500">
                We take full responsibility for your cargo from pickup to
                drop-off.
              </p>
            </div>
          </div>
        </div>
        <div className="grid lg:grid-cols-2 gap-16 items-center mb-28 pt-32">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-12"
          >
            <div className="relative">
              <span className="absolute -top-10 -left-6 text-9xl font-black text-blue-50/50 -z-10 select-none">
                01
              </span>
              <div className="bg-white p-10 rounded-[2.5rem] shadow-2xl shadow-blue-900/5 border border-gray-100 relative overflow-hidden group hover:border-blue-200 transition-all">
                <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50 rounded-bl-full -mr-16 -mt-16 group-hover:scale-110 transition-transform"></div>
                <div className="w-16 h-16 bg-blue-600 text-white rounded-2xl flex items-center justify-center mb-8 shadow-lg shadow-blue-600/20">
                  <Target size={32} />
                </div>
                <h3 className="text-3xl font-black text-gray-900 mb-6 tracking-tight">
                  Our Mission
                </h3>
                <p className="text-lg text-gray-600 leading-relaxed">
                  To provide exceptional logistics solutions that connect
                  businesses and customers seamlessly across Odisha. We are
                  committed to delivering reliable, efficient, and
                  cost-effective transportation and warehousing services
                  tailored to the unique needs of each client.
                </p>
              </div>
            </div>

            <div className="relative">
              <span className="absolute -top-10 -left-6 text-9xl font-black text-blue-50/50 -z-10 select-none">
                02
              </span>
              <div className="bg-[#004a99] p-10 rounded-[2.5rem] shadow-2xl shadow-blue-900/20 border border-blue-800 relative overflow-hidden group hover:bg-[#003d80] transition-all">
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-bl-full -mr-16 -mt-16 group-hover:scale-110 transition-transform"></div>
                <div className="w-16 h-16 bg-white text-[#004a99] rounded-2xl flex items-center justify-center mb-8 shadow-lg">
                  <Eye size={32} />
                </div>
                <h3 className="text-3xl font-black text-white mb-6 tracking-tight">
                  Our Vision
                </h3>
                <p className="text-lg text-blue-100 leading-relaxed">
                  To be the leading logistics service provider in Odisha,
                  renowned for our commitment to quality, sustainability, and
                  customer service. We strive to expand our reach, continuously
                  improving our services and infrastructure to support the
                  evolving needs of the market.
                </p>
              </div>
            </div>
          </motion.div>

          <div className="relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="rounded-[3rem] overflow-hidden shadow-2xl border-8 border-white relative z-10"
            >
              <img
                src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&q=80&w=1000"
                alt="Logistics Excellence"
                className="w-full h-[700px] object-cover"
                referrerPolicy="no-referrer"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#004a99]/80 via-transparent to-transparent"></div>
              <div className="absolute bottom-12 left-12 right-12 text-white">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-1 bg-blue-400 rounded-full"></div>
                  <span className="text-sm font-bold uppercase tracking-[0.3em] text-blue-200">
                    Excellence in Motion
                  </span>
                </div>
                <h4 className="text-4xl font-black leading-tight">
                  Driving the Future of Odisha's Supply Chain
                </h4>
              </div>
            </motion.div>
            <div className="absolute -top-12 -right-12 w-64 h-64 bg-blue-100 rounded-full blur-3xl -z-10 opacity-50"></div>
            <div className="absolute -bottom-12 -left-12 w-64 h-64 bg-blue-200 rounded-full blur-3xl -z-10 opacity-50"></div>
          </div>
        </div>
      </div>
    </section>
  );
};
const CoreValues = () => {
  const values = [
    {
      title: "SAFETY",
      desc: "Safety comes first. We invest in jobsite safety and continuing education to provide a safe working environment for everyone.",
      icon: <Shield className="text-blue-600" size={32} />,
    },
    {
      title: "CUSTOMER SERVICE",
      desc: "We continually improve our training, tools and processes to increase the quality of service we provide our clients.",
      icon: <HelpCircle className="text-blue-600" size={32} />,
    },
    {
      title: "QUALITY",
      desc: "We'll meet your expectations, then we'll exceed them. Our team members will prove themselves through success and quality.",
      icon: <Star className="text-blue-600" size={32} />,
    },
    {
      title: "INTEGRITY",
      desc: "Your best interest is our only interest. We move forward using our core values to establish mutual trust.",
      icon: <CheckCircle2 className="text-blue-600" size={32} />,
    },
  ];

  return (
    <section className="py-24 bg-[#004a99] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold mb-4 uppercase tracking-widest">
            Core Values
          </h2>
          <div className="w-24 h-1 bg-white mx-auto rounded-full"></div>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {values.map((v, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="p-8 rounded-3xl bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 transition-all"
            >
              <div className="mb-6 p-4 bg-white rounded-2xl inline-block">
                {v.icon}
              </div>
              <h3 className="text-xl font-bold mb-3 tracking-wider">
                {v.title}
              </h3>
              <p className="text-blue-100 leading-relaxed text-sm">{v.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
const Testimonials = () => {
  const reviews = [
    {
      name: "Rajesh Mohanty",
      role: "Business Owner",
      content:
        "OPS has been our logistics partner for 2 years. Their reliability in reaching remote parts of Odisha is unmatched.",
      avatar: "https://i.pravatar.cc/150?u=rajesh",
    },
    {
      name: "Priyanka Dash",
      role: "E-commerce Seller",
      content:
        "The express delivery service is a lifesaver. My customers in Sambalpur get their orders within 24 hours!",
      avatar: "https://i.pravatar.cc/150?u=priyanka",
    },
    {
      name: "Amit Patnaik",
      role: "Individual User",
      content:
        "Booked a pickup for some documents. The agent arrived on time and the tracking was very accurate.",
      avatar: "https://i.pravatar.cc/150?u=amit",
    },
  ];

  return (
    <section className="py-24 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            What Our Clients Say
          </h2>
          <p className="text-gray-600">
            Trusted by thousands of businesses and individuals across the state.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {reviews.map((r, i) => (
            <motion.div
              key={i}
              whileHover={{ y: -5 }}
              className="p-8 rounded-3xl bg-gray-50 border border-gray-100 relative"
            >
              <div className="flex items-center gap-4 mb-6">
                <img
                  src={r.avatar}
                  alt={r.name}
                  className="w-12 h-12 rounded-full object-cover"
                  referrerPolicy="no-referrer"
                />
                <div>
                  <h4 className="font-bold text-gray-900">{r.name}</h4>
                  <p className="text-xs text-blue-600 font-bold uppercase tracking-wider">
                    {r.role}
                  </p>
                </div>
              </div>
              <p className="text-gray-600 italic leading-relaxed">
                "{r.content}"
              </p>
              <div className="flex gap-1 mt-6">
                {[1, 2, 3, 4, 5].map((_, i) => (
                  <Star
                    key={i}
                    size={16}
                    className="fill-yellow-400 text-yellow-400"
                  />
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
const Clients = () => {
  const clients = [
    {
      name: "Board of Secondary Education Orissa",
      logo: client1,
    },
    {
      name: "Shree Maruti",
      logo: client2,
    },
  ];

  return (
    <section className="py-20 bg-gray-50/50 border-y border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-2xl font-bold text-gray-400 uppercase tracking-[0.3em]">
            Our Trusted Clients
          </h2>
        </div>
        <div className="flex flex-wrap justify-center items-center gap-12 lg:gap-24 opacity-70 grayscale hover:grayscale-0 transition-all">
          {clients.map((c, i) => (
            <img
              key={i}
              src={c.logo}
              alt={c.name}
              className="h-20 lg:h-52 object-contain"
              referrerPolicy="no-referrer"
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const FAQItem = ({ faq, isOpen, onToggle }) => {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm h-fit">
      <button
        onClick={onToggle}
        className="w-full px-8 py-6 flex justify-between items-center text-left hover:bg-gray-50 transition-colors"
      >
        <span className="font-bold text-gray-900 text-lg">{faq.q}</span>
        <ChevronDown
          className={`text-gray-400 transition-transform duration-300 shrink-0 ml-4 ${isOpen ? "rotate-180" : ""}`}
        />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="px-8 pb-6 text-gray-600 leading-relaxed border-t border-gray-50 pt-4">
              {faq.a}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const FAQ = () => {
  const [openIndices, setOpenIndices] = useState([0]);

  const faqs = [
    {
      q: "What areas in Odisha do you cover?",
      a: "We cover all 30 districts of Odisha, including major cities like Bhubaneswar, Cuttack, Rourkela, Sambalpur, and Berhampur, as well as remote rural areas.",
    },
    {
      q: "How can I track my shipment?",
      a: "Simply enter your unique Tracking ID in the 'Track Shipment' section on our homepage to see real-time updates on your parcel's location.",
    },
    {
      q: "What items are prohibited for delivery?",
      a: "We do not carry hazardous materials, explosives, illegal substances, or currency. Please contact our support for a detailed list of restricted items.",
    },
    {
      q: "Do you provide insurance for high-value items?",
      a: "Yes, we offer optional insurance coverage for high-value shipments to ensure peace of mind for our customers.",
    },
    {
      q: "How long does local delivery take?",
      a: "For intra-city deliveries within major hubs like Bhubaneswar, we offer same-day or next-day delivery. Inter-district transit typically takes 24-48 hours.",
    },
    {
      q: "Can I schedule a specific pickup time?",
      a: "Yes, our 'Book Pickup' feature allows you to select a preferred date and time slot that works best for your schedule.",
    },
    {
      q: "What are your customer support hours?",
      a: "Our dedicated customer support team is available 24/7 to assist you with any queries, tracking issues, or service requests.",
    },
    {
      q: "Do you offer bulk shipping for businesses?",
      a: "Absolutely! We have specialized corporate plans with discounted rates and dedicated account managers for high-volume business shipments.",
    },
  ];

  const toggleIndex = (index) => {
    setOpenIndices((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    );
  };

  return (
    <section id="faq" className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-100 text-[#004a99] text-sm font-bold mb-4">
            <HelpCircle size={16} />
            <span>Got Questions?</span>
          </div>
          <h2 className="text-4xl font-bold text-gray-900">
            Frequently Asked Questions
          </h2>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {faqs.map((faq, i) => (
            <FAQItem
              key={i}
              faq={faq}
              isOpen={openIndices.includes(i)}
              onToggle={() => toggleIndex(i)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const Team = () => {
  const members = [
    {
      name: "Suryakant Samal",
      role: "Founder and CEO",
      desc: "Suryakant Samal, founder and CEO of Odisha Parcel Service Private Limited, brings 15 years of extensive experience in logistics operations. Having worked with industry leaders like First Flight, Gati LTD, DTDC Express LTD, Vulcan Express, and Delhivery Limited, Suryakant's expertise in optimizing logistics operations ensures the company’s continued growth and success.",
      avatar: team1,
    },
    {
      name: "Chandana Nayak",
      role: "Co Founder",
      desc: "Chandana Nayak, the Co founder of Odisha Parcel Service Private Limited, brings over 10 years of valuable experience in customer support and business development from her previous role at Pustak Mandir and Delhivery LTD. With a strong foundation in handling customer needs and driving business growth, Chandana established Odisha Parcel Service to provide efficient, reliable, and timely parcel delivery services across the region.",
      avatar: team2,
    },
    {
      name: "Subhashree Das",
      role: "Co Founder",
      desc: "Subhashree Das, the co-founder of Odisha Parcel Service Private Limited, brings 5 years of valuable experience in the logistics industry, specializing in customer support and air cargo. Having worked with Indian Airlines, Subhashree has gained deep insight into the complexities of air cargo operations and customer service, enabling her to contribute significantly to the growth of Odisha Parcel Service.",
      avatar: team3,
    },
    {
      name: "Himanshu Jena",
      role: "VP Operations",
      desc: "Himanshu Jena, VP of Operations at Odisha Parcel Service Private Limited, brings 10 years of experience in logistics operations. Having worked with renowned companies like First Flight, Armex Limited, Xpressbees, and Delhivery Limited, Himanshu plays a key role in optimizing operational efficiency and ensuring seamless service delivery.",
      avatar: team4,
    },
  ];

  return (
    <section id="team" className="bg-white py-24 relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(#004a99_1px,transparent_1px)] [background-size:32px_32px]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Heading */}
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 text-[#004a99] text-xs font-bold uppercase tracking-widest mb-4 border border-blue-100"
          >
            <ShieldCheck size={14} />
            Our Leadership
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            viewport={{ once: true }}
            className="text-4xl font-black text-gray-900 tracking-tight"
          >
            The Minds Behind <span className="text-[#004a99]">OPS</span>
          </motion.h2>
        </div>

        {/* Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 items-stretch">
          {members.map((m, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              viewport={{ once: true }}
              className="group h-full"
            >
              <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-sm hover:shadow-xl hover:border-blue-100 transition-all duration-500 h-full flex flex-col items-center text-center">
                {/* Image */}
                <div className="relative mb-4 w-full max-w-[220px] h-[250px]">
                  <div className="absolute -inset-3 bg-blue-50 rounded-[2rem] scale-0 group-hover:scale-100 transition-transform duration-500"></div>
                  <img
                    src={m.avatar}
                    alt={m.name}
                    className="relative w-full h-full rounded-2xl object-cover border-4 border-white shadow-md transition-transform duration-500 group-hover:scale-[1.03]"
                  />
                </div>

                {/* Name */}
                <h3 className="text-xl font-bold text-gray-900 mb-1 group-hover:text-[#004a99] transition-colors min-h-[48px] flex items-center justify-center">
                  {m.name}
                </h3>

                {/* Role */}
                <p className="text-blue-600 font-bold text-xs uppercase tracking-widest mb-4">
                  {m.role}
                </p>

                {/* Description */}
                <p className="text-gray-500 text-sm leading-relaxed text-justify mb-6 flex-grow text-center">
                  {m.desc}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const HeroAnimation = () => {
  return (
    <div className="relative w-full h-[450px]  sm:h-[500px] lg:h-[600px] flex items-center justify-center scale-[0.8] sm:scale-90 lg:scale-[0.95] transition-transform duration-500">
      {/* Background Network Nodes & Lines */}
      <div className="absolute inset-0 -z-10 opacity-20">
        <svg width="100%" height="100%" className="text-blue-200">
          <pattern
            id="dots"
            x="0"
            y="0"
            width="40"
            height="40"
            patternUnits="userSpaceOnUse"
          >
            <circle cx="2" cy="2" r="1.5" fill="currentColor" />
          </pattern>
          <rect width="100%" height="100%" fill="url(#dots)" />

          {/* Connecting Lines to floating elements */}
          <motion.path
            d="M250,325 L100,150"
            fill="none"
            stroke="currentColor"
            strokeWidth="1"
            strokeDasharray="5,5"
            animate={{ strokeDashoffset: [0, -50] }}
            transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
          />
          <motion.path
            d="M250,325 L400,500"
            fill="none"
            stroke="currentColor"
            strokeWidth="1"
            strokeDasharray="5,5"
            animate={{ strokeDashoffset: [0, 50] }}
            transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
          />
        </svg>
      </div>

      {/* Background Glows */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[140%] h-[140%] bg-blue-500/5 rounded-full blur-[120px]"></div>

      {/* Main Parcel Box - LARGER */}
      <motion.div
        animate={{
          y: [0, -30, 0],
          rotate: [0, 3, 0],
        }}
        transition={{
          duration: 5,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        className="relative z-20 bg-white p-8 lg:p-14 rounded-[2.5rem] lg:rounded-[3.5rem] shadow-[0_50px_100px_-20px_rgba(0,74,153,0.15)] border border-blue-50 flex flex-col items-center gap-4 lg:gap-6"
      >
        <div className="w-44 h-44 lg:w-52 lg:h-52 bg-gradient-to-br from-blue-50 to-white rounded-[2rem] lg:rounded-[2.5rem] flex items-center justify-center text-[#004a99] shadow-inner">
          <Package
            size={64}
            strokeWidth={1}
            className="lg:size-24 lg:scale-125"
          />
        </div>
        <div className="text-center">
          <h3 className="font-black text-2xl lg:text-4xl text-gray-900 tracking-tight">
            OPS Premium
          </h3>
          <p className="text-blue-600 font-bold text-[10px] lg:text-base uppercase tracking-[0.3em] mt-1 lg:mt-2">
            Priority Transit
          </p>
        </div>

        {/* Progress Bar */}
        <div className="w-full h-2 lg:h-3 bg-gray-100 rounded-full overflow-hidden mt-2 lg:mt-4 p-0.5">
          <motion.div
            initial={{ width: "0%" }}
            animate={{ width: "85%" }}
            transition={{ duration: 2.5, delay: 1, ease: "easeOut" }}
            className="h-full bg-gradient-to-r from-[#004a99] to-blue-400 rounded-full relative"
          >
            <div className="absolute top-0 right-0 w-2 h-full bg-white/40 animate-pulse"></div>
          </motion.div>
        </div>
      </motion.div>

      {/* Floating Tracking Card */}
      <motion.div
        initial={{ opacity: 0, x: 50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.5 }}
        className="absolute top-0 right-0 lg:-right-10 z-40 bg-white/90 backdrop-blur-md p-4 lg:p-6 rounded-2xl lg:rounded-3xl shadow-2xl border border-white/50 max-w-[160px] sm:max-w-[180px] lg:max-w-[240px]"
      >
        <div className="flex items-center justify-between mb-3 lg:mb-4">
          <span className="text-[8px] lg:text-[10px] font-black text-blue-600 uppercase tracking-widest">
            Live Tracking
          </span>
          <div className="w-1.5 h-1.5 lg:w-2 lg:h-2 bg-green-500 rounded-full animate-ping"></div>
        </div>
        <div className="space-y-2 lg:space-y-3">
          <div className="flex items-center gap-2 lg:gap-3">
            <div className="w-6 h-6 lg:w-8 lg:h-8 bg-blue-100 rounded-lg flex items-center justify-center text-[#004a99]">
              <MapPin size={12} />
            </div>
            <div>
              <p className="text-[8px] lg:text-[10px] text-gray-400 font-bold">
                Current Location
              </p>
              <p className="text-[10px] lg:text-xs font-bold text-gray-800">
                Bhubaneswar
              </p>
            </div>
          </div>
          <div className="h-4 lg:h-6 w-0.5 bg-blue-100 ml-3 lg:ml-4"></div>
          <div className="flex items-center gap-2 lg:gap-3">
            <div className="w-6 h-6 lg:w-8 lg:h-8 bg-gray-100 rounded-lg flex items-center justify-center text-gray-400">
              <Truck size={12} />
            </div>
            <div>
              <p className="text-[8px] lg:text-[10px] text-gray-400 font-bold">
                Next Stop
              </p>
              <p className="text-[10px] lg:text-xs font-bold text-gray-400">
                Cuttack Hub
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Floating Elements */}
      <motion.div
        animate={{ x: [0, 15, 0], y: [0, -20, 0] }}
        transition={{
          duration: 7,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 0.2,
        }}
        className="absolute top-1/4 left-0 lg:-left-20 z-30 bg-white p-3 lg:p-5 rounded-xl lg:rounded-2xl shadow-xl border border-green-50 flex items-center gap-3 lg:gap-4"
      >
        <div className="w-8 h-8 lg:w-12 lg:h-12 bg-green-100 text-green-600 rounded-lg lg:rounded-xl flex items-center justify-center">
          <Shield size={16} />
        </div>
        <div>
          <p className="font-black text-gray-900 text-[10px] lg:text-sm">
            Insured
          </p>
          <p className="text-[8px] lg:text-[10px] text-gray-500 font-bold">
            Up to ₹50k
          </p>
        </div>
      </motion.div>

      <motion.div
        animate={{ x: [0, -15, 0], y: [0, 20, 0] }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 1.5,
        }}
        className="absolute bottom-1/4 right-0 lg:-right-20 z-30 bg-white p-3 lg:p-5 rounded-xl lg:rounded-2xl shadow-xl border border-blue-50 flex items-center gap-3 lg:gap-4"
      >
        <div className="w-8 h-8 lg:w-12 lg:h-12 bg-blue-100 text-[#004a99] rounded-lg lg:rounded-xl flex items-center justify-center">
          <Clock size={16} />
        </div>
        <div>
          <p className="font-black text-gray-900 text-[10px] lg:text-sm">
            Express
          </p>
          <p className="text-[8px] lg:text-[10px] text-gray-500 font-bold">
            24h Delivery
          </p>
        </div>
      </motion.div>

      {/* Small Floating Icons */}
      <motion.div
        animate={{ y: [0, -10, 0] }}
        transition={{ duration: 3, repeat: Infinity }}
        className="absolute top-10 left-1/4 text-blue-200 opacity-40"
      >
        <Search size={24} />
      </motion.div>
      <motion.div
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 4, repeat: Infinity }}
        className="absolute bottom-20 right-1/4 text-red-200 opacity-40"
      >
        <HelpCircle size={24} />
      </motion.div>
      <motion.div
        animate={{ scale: [1, 1.2, 1] }}
        transition={{ duration: 5, repeat: Infinity }}
        className="absolute top-1/2 -right-10 text-green-200 opacity-30 hidden lg:block"
      >
        <CheckCircle2 size={48} />
      </motion.div>

      {/* Animated Truck crossing - LARGER */}
      <div className="absolute bottom-0 w-full overflow-hidden h-32 lg:h-40 pointer-events-none opacity-5">
        <motion.div
          initial={{ x: "-100%" }}
          animate={{ x: "250%" }}
          transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
          className="flex items-center gap-4 lg:gap-6"
        >
          <Truck size={80} className="text-[#004a99]" />
          <div className="h-1 lg:h-2 w-48 lg:w-64 bg-[#004a99] rounded-full"></div>
          <Package size={40} className="text-[#004a99]" />
        </motion.div>
      </div>
    </div>
  );
};

const Hero = () => {
  return (
    <section
      id="home"
      className="relative overflow-hidden min-h-screen flex items-center pt-28 lg:pt-14 bg-gradient-to-br from-blue-50 via-white to-red-50"
    >
      {/* Background Decorative Elements */}
      <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-blue-400/5 rounded-full blur-[150px] -z-10 translate-x-1/2 -translate-y-1/2"></div>
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-red-400/5 rounded-full blur-[150px] -z-10 -translate-x-1/2 translate-y-1/2"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="text-center lg:text-left"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-100 text-[#004a99] text-sm font-bold mb-6">
              <Truck size={16} />
              <span>Odisha's Most Trusted Logistics Partner</span>
            </div>
            <h1 className="text-5xl lg:text-7xl font-extrabold text-gray-900 leading-tight mb-6 tracking-tight">
              Your Cargo, <br />
              Our{" "}
              <span className="text-[#004a99] relative">
                Priority
                <svg
                  className="absolute -bottom-2 left-0 w-full h-3 text-red-400/30"
                  viewBox="0 0 100 10"
                  preserveAspectRatio="none"
                >
                  <path
                    d="M0 5 Q 25 0, 50 5 T 100 5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="8"
                  />
                </svg>
              </span>
            </h1>
            <p className="text-xl text-gray-600 mb-10 max-w-lg mx-auto lg:mx-0 leading-relaxed">
              Odisha Parcel Service (OPS) provides reliable, secure, and
              lightning-fast delivery solutions across the state.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <a
                href="#pickup"
                className="px-8 py-4 bg-[#004a99] text-white rounded-xl font-bold text-lg hover:bg-[#003d80] transition-all flex items-center justify-center gap-2 shadow-xl shadow-blue-900/20"
              >
                Book a Pickup <ChevronRight size={20} />
              </a>
              <a
                href="#tracking"
                className="px-8 py-4 bg-white text-gray-900 border-2 border-gray-100 rounded-xl font-bold text-lg hover:border-[#004a99] transition-all flex items-center justify-center gap-2 shadow-sm"
              >
                Track Shipment <Search size={20} />
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9, rotate: 2 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="relative w-full pt-0 lg:pt-20"
          >
            <HeroAnimation />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const Services = () => {
  const services = [
    {
      icon: <Package className="text-blue-600" size={32} />,
      title: "LTL (Less than Truck Load)",
      desc: "Cost-effective shipping for smaller shipments. Share space with other shipments and reduce transportation costs.",
    },
    {
      icon: <Truck className="text-red-600" size={32} />,
      title: "FTL (Full Truck Load)",
      desc: "Dedicated trucks for larger shipments. Faster delivery and direct transportation without intermediate stops.",
    },
    {
      icon: <Shield className="text-green-600" size={32} />,
      title: "3PL Warehousing",
      desc: "Reliable solutions for storage, inventory management, and distribution. Strategically located for easy access.",
    },
    {
      icon: <Clock className="text-orange-600" size={32} />,
      title: "Priority Transit",
      desc: "Ensuring timely and secure delivery across all districts in Odisha with exceptional customer satisfaction.",
    },
  ];

  return (
    <section id="services" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Our Services
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            We offer a wide range of logistics services tailored to meet your
            specific delivery needs.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {services.map((s, i) => (
            <motion.div
              key={i}
              whileHover={{ y: -10 }}
              className="p-8 rounded-3xl bg-gray-50 border border-gray-100 hover:border-blue-200 hover:shadow-xl transition-all group"
            >
              <div className="mb-6 p-4 bg-white rounded-2xl inline-block shadow-sm group-hover:scale-110 transition-transform">
                {s.icon}
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                {s.title}
              </h3>
              <p className="text-gray-600 leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const PickupForm = () => {
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    customerName: "",
    phoneNumber: "",
    address: "",
    scheduleDate: "",
    slotTime: "",
    parcelsNumber: "",
    weight: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const payload = {
        customerName: formData.customerName,
        customerNumber: formData.phoneNumber,
        address: formData.address,
        scheduleDate: formData.scheduleDate,
        slotTime: formData.slotTime,
        parcelsNumber: Number(formData.parcelsNumber) || 1,
        parcelsWeight: Number(formData.weight) || 1,
        pickupDate: new Date().toISOString(),
        time: new Date().toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        }),
      };

      const res = await createPickUpService(payload);

      if (res?.status === 200 || res?.status === 201 || res?.data) {
        setSubmitted(true);
        setFormData({
          customerName: "",
          phoneNumber: "",
          address: "",
          parcelsNumber: "",
          weight: "",
        });
        setTimeout(() => setSubmitted(false), 5000);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to book pickup. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section id="pickup" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-[2.5rem] shadow-2xl overflow-hidden border border-gray-100">
          <div className="grid md:grid-cols-5">
            <div className="md:col-span-2 bg-[#004a99] p-6 lg:p-12 text-white flex flex-col justify-between relative overflow-hidden">
              <div className="relative z-10">
                <h2 className="text-3xl font-bold mb-6">Book a Pickup</h2>
                <p className="text-blue-100 mb-10 text-lg">
                  Fill in the details and our team will reach out to you for the
                  pickup within 2 hours.
                </p>

                <div className="space-y-8">
                  <div className="flex items-center gap-5">
                    <div className="p-3 bg-white/10 rounded-xl">
                      <Phone size={24} />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-xs text-blue-200 font-bold uppercase tracking-wider">
                        Call Us
                      </span>
                      <span className="font-bold lg:text-base text-sm">
                        +91 7846983527
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-5">
                    <div className="p-3 bg-white/10 rounded-xl">
                      <Mail size={24} />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-xs text-blue-200 font-bold uppercase tracking-wider">
                        Email Us
                      </span>
                      <span className="font-bold lg:text-base text-sm">
                        customersupport@odishaparcel.com
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-5">
                    <div className="p-3 bg-white/10 rounded-xl">
                      <MapPin size={24} />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-xs text-blue-200 font-bold uppercase tracking-wider">
                        Visit Us
                      </span>
                      <span className="font-bold lg:text-base text-sm">
                        Bhubaneswar, Odisha
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-12 pt-8 border-t border-white/10 relative z-10">
                <p className="text-sm text-blue-200 italic font-medium">
                  "Your Cargo, Our Priority"
                </p>
              </div>

              {/* Decorative circle */}
              <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-white/5 rounded-full blur-3xl"></div>
            </div>

            <div className="md:col-span-3 p-6 lg:p-12">
              {submitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="h-full flex flex-col items-center justify-center text-center py-20"
                >
                  <div className="w-24 h-24 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-8 shadow-inner">
                    <CheckCircle2 size={48} />
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900 mb-3">
                    Request Received!
                  </h3>
                  <p className="text-gray-600 text-lg">
                    Our agent will call you shortly to confirm the pickup
                    details.
                  </p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-8">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-gray-700 uppercase tracking-wider">
                        Full Name
                      </label>
                      <input
                        required
                        type="text"
                        value={formData.customerName}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            customerName: e.target.value,
                          })
                        }
                        className="w-full px-5 py-4 rounded-2xl border border-gray-200 focus:border-[#004a99] focus:ring-4 focus:ring-blue-50 outline-none transition-all"
                        placeholder="John Doe"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-gray-700 uppercase tracking-wider">
                        Phone Number
                      </label>
                      <input
                        required
                        type="tel"
                        value={formData.phoneNumber}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            phoneNumber: e.target.value,
                          })
                        }
                        className="w-full px-5 py-4 rounded-2xl border border-gray-200 focus:border-[#004a99] focus:ring-4 focus:ring-blue-50 outline-none transition-all"
                        placeholder="+91 ..."
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-bold text-gray-700 uppercase tracking-wider">
                      Pickup Address
                    </label>
                    <textarea
                      required
                      value={formData.address}
                      onChange={(e) =>
                        setFormData({ ...formData, address: e.target.value })
                      }
                      className="w-full px-5 py-4 rounded-2xl border border-gray-200 focus:border-[#004a99] focus:ring-4 focus:ring-blue-50 outline-none transition-all h-32 resize-none"
                      placeholder="Enter full address with pincode"
                    ></textarea>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-gray-700 uppercase tracking-wider">
                        Pickup Schedule Date
                      </label>
                      <input
                        required
                        type="date"
                        value={formData.scheduleDate}
                        onChange={(e) =>
                          setFormData({ ...formData, scheduleDate: e.target.value })
                        }
                        className="w-full px-5 py-4 rounded-2xl border border-gray-200 focus:border-[#004a99] focus:ring-4 focus:ring-blue-50 outline-none transition-all"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-gray-700 uppercase tracking-wider">
                        Slot Time
                      </label>
                      <input
                        required
                        type="time"
                        value={formData.slotTime}
                        onChange={(e) =>
                          setFormData({ ...formData, slotTime: e.target.value })
                        }
                        className="w-full px-5 py-4 rounded-2xl border border-gray-200 focus:border-[#004a99] focus:ring-4 focus:ring-blue-50 outline-none transition-all"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-gray-700 uppercase tracking-wider">
                        Total Parcel Number
                      </label>
                      <input
                        type="number"
                        min="1"
                        required
                        value={formData.parcelsNumber}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            parcelsNumber: e.target.value,
                          })
                        }
                        className="w-full px-5 py-4 rounded-2xl border border-gray-200 focus:border-[#004a99] focus:ring-4 focus:ring-blue-50 outline-none transition-all"
                        placeholder="e.g. 5"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-gray-700 uppercase tracking-wider">
                        Approx. Weight
                      </label>
                      <input
                        type="text"
                        value={formData.weight}
                        onChange={(e) =>
                          setFormData({ ...formData, weight: e.target.value })
                        }
                        className="w-full px-5 py-4 rounded-2xl border border-gray-200 focus:border-[#004a99] focus:ring-4 focus:ring-blue-50 outline-none transition-all"
                        placeholder="e.g. 2kg"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full py-5 bg-[#004a99] text-white rounded-2xl font-bold text-xl hover:bg-[#003d80] transition-all shadow-xl shadow-blue-900/20 active:scale-[0.98] disabled:opacity-70 flex justify-center items-center gap-2"
                  >
                    {isLoading ? (
                      <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    ) : (
                      "Confirm Pickup Request"
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Tracking = () => {
  const [trackingId, setTrackingId] = useState("");
  const [status, setStatus] = useState(null);

  const handleTrack = (e) => {
    e.preventDefault();
    if (!trackingId) return;
    setStatus("searching");
    setTimeout(() => setStatus("found"), 1500);
  };

  return (
    <section id="tracking" className="py-24 bg-white border-y border-gray-100">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="text-4xl font-bold text-gray-900 mb-4">
          Track Your Shipment
        </h2>
        <p className="text-gray-600 mb-12 text-lg">
          Enter your tracking ID to see the real-time status of your parcel.
        </p>

        <form
          onSubmit={handleTrack}
          className="flex flex-col sm:flex-row gap-4 mb-16"
        >
          <input
            type="text"
            value={trackingId}
            onChange={(e) => setTrackingId(e.target.value)}
            placeholder="Enter Tracking ID (e.g. OPS123456)"
            className="flex-1 px-8 py-5 rounded-2xl border-2 border-gray-100 focus:border-[#004a99] outline-none transition-all text-xl font-medium shadow-sm"
          />
          <button
            type="submit"
            className="px-12 py-5 bg-[#004a99] text-white rounded-2xl font-bold text-xl hover:bg-[#003d80] transition-all shadow-xl shadow-blue-900/20 flex items-center justify-center gap-3"
          >
            Track Now <Search size={24} />
          </button>
        </form>

        {status === "searching" && (
          <div className="flex flex-col items-center gap-6 py-10">
            <div className="w-16 h-16 border-4 border-blue-100 border-t-[#004a99] rounded-full animate-spin"></div>
            <p className="text-gray-500 font-bold text-lg">
              Searching our database...
            </p>
          </div>
        )}

        {status === "found" && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-blue-50/50 p-10 rounded-[2.5rem] border border-blue-100 text-left shadow-inner"
          >
            <div className="flex flex-col sm:flex-row justify-between items-start gap-6 mb-12">
              <div>
                <p className="text-xs text-blue-600 font-bold uppercase tracking-[0.2em] mb-2">
                  Current Status
                </p>
                <h3 className="text-3xl font-extrabold text-gray-900">
                  In Transit
                </h3>
              </div>
              <div className="sm:text-right">
                <p className="text-xs text-gray-500 font-bold uppercase tracking-[0.2em] mb-2">
                  Estimated Delivery
                </p>
                <p className="text-xl font-extrabold text-gray-900">
                  Tomorrow, 5:00 PM
                </p>
              </div>
            </div>

            <div className="relative pl-10 sm:pl-12">
              <div className="absolute left-4 sm:left-5 top-2 bottom-2 w-1 bg-blue-200 rounded-full"></div>
              <div className="space-y-12 relative">
                <div className="flex gap-8 items-start">
                  <div className="w-10 h-10 rounded-full bg-green-500 border-4 border-white shadow-lg z-10 flex items-center justify-center shrink-0 -ml-10 sm:-ml-12">
                    <CheckCircle2 size={20} className="text-white" />
                  </div>
                  <div>
                    <p className="font-extrabold text-gray-900 text-lg">
                      Pickup Completed
                    </p>
                    <p className="text-gray-500 font-medium">
                      Bhubaneswar Hub • 10:30 AM, 02 April
                    </p>
                  </div>
                </div>
                <div className="flex gap-8 items-start">
                  <div className="w-10 h-10 rounded-full bg-blue-500 border-4 border-white shadow-lg z-10 flex items-center justify-center shrink-0 -ml-10 sm:-ml-12 animate-pulse">
                    <Truck size={20} className="text-white" />
                  </div>
                  <div>
                    <p className="font-extrabold text-gray-900 text-lg">
                      Dispatched from Hub
                    </p>
                    <p className="text-gray-500 font-medium">
                      Bhubaneswar Hub • 02:15 PM, 02 April
                    </p>
                  </div>
                </div>
                <div className="flex gap-8 items-start opacity-40">
                  <div className="w-10 h-10 rounded-full bg-gray-300 border-4 border-white shadow-lg z-10 flex items-center justify-center shrink-0 -ml-10 sm:-ml-12">
                    <MapPin size={20} className="text-white" />
                  </div>
                  <div>
                    <p className="font-extrabold text-gray-900 text-lg">
                      Out for Delivery
                    </p>
                    <p className="text-gray-500 font-medium">Pending</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
};

const Footer = () => {
  return (
    <footer className="bg-gray-950 text-white pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-16 mb-20">
          <div className="col-span-1 lg:col-span-2">
            <div className="flex items-center gap-4 mb-8">
              <img
                src={companyLogo}
                alt="OPS Logo"
                className="h-14 w-14 object-contain bg-white rounded-2xl p-1.5"
                referrerPolicy="no-referrer"
              />
              <span className="text-3xl font-black ">
                Odisha Parcel Service
              </span>
            </div>
            <p className="text-gray-400 max-w-md mb-10 text-sm leading-relaxed">
              Odisha Parcel Service Pvt. Ltd. is a leading logistics provider
              committed to delivering your cargo with safety and speed across
              the heart of Odisha.
            </p>
            {/* <div className="flex gap-4 mb-10">
              <a
                href="#"
                className="w-12 h-12 bg-white/5 rounded-xl flex items-center justify-center hover:bg-[#004a99] transition-all"
              >
                <Facebook size={20} />
              </a>
              <a
                href="#"
                className="w-12 h-12 bg-white/5 rounded-xl flex items-center justify-center hover:bg-[#004a99] transition-all"
              >
                <Twitter size={20} />
              </a>
              <a
                href="#"
                className="w-12 h-12 bg-white/5 rounded-xl flex items-center justify-center hover:bg-[#004a99] transition-all"
              >
                <Instagram size={20} />
              </a>
              <a
                href="#"
                className="w-12 h-12 bg-white/5 rounded-xl flex items-center justify-center hover:bg-[#004a99] transition-all"
              >
                <Linkedin size={20} />
              </a>
            </div> */}
            {/* <div className="bg-white/5 p-8 rounded-[2rem] border border-white/10 inline-block">
              <img
                src="input_file_1.png"
                alt="Company Details"
                className="h-20 object-contain"
                referrerPolicy="no-referrer"
              />
            </div> */}
          </div>

          <div>
            <h4 className="text-xl font-bold mb-8 text-blue-400">
              Quick Links
            </h4>
            <ul className="space-y-5 text-gray-400 text-sm">
              <li>
                <a
                  href="#home"
                  className="hover:text-white transition-colors flex items-center gap-2 group"
                >
                  <ChevronRight
                    size={16}
                    className="group-hover:translate-x-1 transition-transform"
                  />{" "}
                  Home
                </a>
              </li>
              <li>
                <a
                  href="#services"
                  className="hover:text-white transition-colors flex items-center gap-2 group"
                >
                  <ChevronRight
                    size={16}
                    className="group-hover:translate-x-1 transition-transform"
                  />{" "}
                  Services
                </a>
              </li>
              <li>
                <a
                  href="#why-us"
                  className="hover:text-white transition-colors flex items-center gap-2 group"
                >
                  <ChevronRight
                    size={16}
                    className="group-hover:translate-x-1 transition-transform"
                  />{" "}
                  Why Choose Us
                </a>
              </li>
              <li>
                <a
                  href="#tracking"
                  className="hover:text-white transition-colors flex items-center gap-2 group"
                >
                  <ChevronRight
                    size={16}
                    className="group-hover:translate-x-1 transition-transform"
                  />{" "}
                  Track Shipment
                </a>
              </li>
              <li>
                <a
                  href="#pickup"
                  className="hover:text-white transition-colors flex items-center gap-2 group"
                >
                  <ChevronRight
                    size={16}
                    className="group-hover:translate-x-1 transition-transform"
                  />{" "}
                  Book Pickup
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-xl font-bold mb-8 text-blue-400">
              Contact Info
            </h4>
            <ul className="space-y-6 text-gray-400 text-sm">
              <li className="flex items-center gap-4">
                <Mail size={24} className="text-blue-500 shrink-0" />
                <span>customersupport@odishaparcel.com</span>
              </li>
              <li className="flex items-center gap-4">
                <Globe size={24} className="text-blue-500 shrink-0" />
                <span>www.odishaparcel.com</span>
              </li>
              <li className="flex items-start gap-4">
                <MapPin size={24} className="text-blue-500 shrink-0 mt-1" />
                <span className="leading-relaxed">
                  Reg. Address
                  <br />
                  PLOT NO-701/5403, LANE-1, PATITAPABANA NAGAR, Badagarh Brit
                  Colony, Bhubaneswar, Khorda- 751018, Orissa
                  <br />
                  W/H Address <br />
                  Plot no -325, Eden Garden,Pahal, Bhubaneswar,Ofisha-751032
                </span>
              </li>
              {/* <li className="flex items-center gap-4">
                <Phone size={24} className="text-blue-500 shrink-0" />
                <span className="font-bold">+91 98765 43210</span>
              </li> */}
            </ul>
          </div>
        </div>

        <div className="pt-12 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-8 text-gray-500 font-medium">
          <p>© 2026 Odisha Parcel Service Pvt. Ltd. All rights reserved.</p>
          <div className="flex gap-10">
            <a href="#" className="hover:text-white transition-colors">
              Privacy Policy
            </a>
            <a href="#" className="hover:text-white transition-colors">
              Terms of Service
            </a>
            <a href="#" className="hover:text-white transition-colors">
              Sitemap
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

const Coverage = () => {
  const regions = [
    "Northern Odisha",
    "Southern Odisha",
    "Western Odisha",
    "Coastal Odisha",
    "Central Odisha",
  ];

  return (
    <section className="py-24 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Extensive Coverage Across Odisha
            </h2>
            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
              From the bustling streets of Bhubaneswar to the remote villages of
              Malkangiri, our network reaches every nook and corner of the
              state.
            </p>
            <div className="space-y-4">
              {regions.map((region, i) => (
                <div
                  key={i}
                  className="flex items-center gap-4 p-4 bg-gray-50 rounded-2xl border border-gray-100 hover:border-blue-200 transition-all"
                >
                  <div className="w-10 h-10 bg-[#004a99] text-white rounded-full flex items-center justify-center font-bold">
                    {i + 1}
                  </div>
                  <span className="font-bold text-gray-900 text-lg">
                    {region}
                  </span>
                </div>
              ))}
            </div>
          </motion.div>
          <div className="relative">
            <div className="bg-blue-600/5 rounded-[3rem] p-8 sm:p-12 border-2 border-dashed border-blue-200">
              <div className="aspect-square bg-white rounded-[2rem] shadow-xl flex items-center justify-center p-8 relative overflow-hidden">
                {/* Simulated Map SVG */}
                <svg
                  viewBox="0 0 200 200"
                  className="w-full h-full text-blue-100 fill-current"
                >
                  <path d="M50,30 Q80,10 120,30 T170,80 T150,150 T80,170 T30,120 T50,30" />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="relative">
                    <MapPin
                      className="text-[#004a99] animate-bounce"
                      size={48}
                    />
                    <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-8 h-2 bg-black/10 rounded-full blur-sm"></div>
                  </div>
                </div>
                {/* Pulsing dots */}
                <div className="absolute top-1/4 left-1/3 w-3 h-3 bg-red-500 rounded-full animate-ping"></div>
                <div className="absolute bottom-1/3 right-1/4 w-3 h-3 bg-red-500 rounded-full animate-ping delay-300"></div>
                <div className="absolute top-1/2 right-1/3 w-3 h-3 bg-red-500 rounded-full animate-ping delay-700"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const DownloadApp = () => {
  return (
    <section className="py-24 bg-[#004a99] relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="bg-white/10 backdrop-blur-xl rounded-[3rem] p-10 sm:p-20 border border-white/20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl sm:text-5xl font-black text-white mb-6 leading-tight">
                Track on the Go with <br />
                <span className="text-blue-300">OPS Mobile App</span>
              </h2>
              <p className="text-xl text-blue-100 mb-10 leading-relaxed">
                Get real-time notifications, manage your pickups, and track all
                your shipments in one place. Available now for Android and iOS.
              </p>
              <div className="flex flex-col sm:flex-row gap-6">
                <button className="px-8 py-4 bg-white text-gray-900 rounded-2xl font-bold text-lg hover:bg-blue-50 transition-all flex items-center justify-center gap-3 shadow-xl">
                  <img
                    src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                    alt="Google Play"
                    className="h-8"
                  />
                </button>
                <button className="px-8 py-4 bg-gray-900 text-white rounded-2xl font-bold text-lg hover:bg-gray-800 transition-all flex items-center justify-center gap-3 shadow-xl border border-white/10">
                  <img
                    src="https://upload.wikimedia.org/wikipedia/commons/3/3c/Download_on_the_App_Store_Badge.svg"
                    alt="App Store"
                    className="h-8"
                  />
                </button>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <motion.div
                initial={{ y: 50, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                className="relative z-10"
              >
                <img
                  src="https://picsum.photos/seed/mobile-app/400/800"
                  alt="Mobile App"
                  className="w-72 mx-auto rounded-[3rem] border-8 border-gray-900 shadow-2xl"
                  referrerPolicy="no-referrer"
                />
              </motion.div>
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-blue-400/20 rounded-full blur-3xl -z-10"></div>
            </div>
          </div>
        </div>
      </div>
      {/* Decorative background elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2 blur-3xl"></div>
    </section>
  );
};

const ScrollToTop = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const toggleVisible = () => setVisible(window.scrollY > 500);
    window.addEventListener("scroll", toggleVisible);
    return () => window.removeEventListener("scroll", toggleVisible);
  }, []);

  return (
    <AnimatePresence>
      {visible && (
        <motion.button
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.5 }}
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="fixed bottom-8 right-8 z-50 p-4 bg-[#004a99] text-white rounded-2xl shadow-2xl hover:bg-[#003d80] transition-all active:scale-90"
        >
          <ChevronDown className="rotate-180" size={24} />
        </motion.button>
      )}
    </AnimatePresence>
  );
};
const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState();

  const images = [
    {
      url: gallery1,
      title: "Modern Warehousing",
      category: "Infrastructure",
    },
    {
      url: gallery2,
      title: "Our Logistics Fleet",
      category: "Transport",
    },
    {
      url: gallery3,
      title: "Efficient Distribution",
      category: "Operations",
    },
    {
      url: gallery4,
      title: "Last Mile Delivery",
      category: "Service",
    },
    {
      url: gallery5,
      title: "Cargo Management",
      category: "Transport",
    },
    {
      url: gallery6,
      title: "Secure Packaging",
      category: "Operations",
    },
  ];

  return (
    <section id="gallery" className="py-32 bg-gray-50/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 text-blue-600 text-xs font-black uppercase tracking-[0.2em] mb-6"
          >
            <Image size={14} />
            Visual Journey
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-5xl font-black text-gray-900 mb-6 tracking-tight"
          >
            Our <span className="text-[#004a99]">Operations</span> Gallery
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed"
          >
            A glimpse into our daily operations, infrastructure, and the scale
            of our logistics network across Odisha.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {images.map((img, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="group relative cursor-pointer"
              onClick={() => setSelectedImage(img.url)}
            >
              <div className="relative aspect-[4/3] rounded-lg overflow-hidden shadow-2xl shadow-blue-900/5 border border-white">
                <img
                  src={img.url}
                  alt={img.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  referrerPolicy="no-referrer"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500">
                  {/* <div className="absolute bottom-8 left-8 right-8">
                    <span className="text-blue-400 text-xs font-bold uppercase tracking-widest mb-2 block">
                      {img.category}
                    </span>
                    <h3 className="text-white text-2xl font-black">
                      {img.title}
                    </h3>
                  </div> */}
                  <div className="absolute top-8 right-8 w-12 h-12 bg-white/10 backdrop-blur-md rounded-2xl flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-all duration-500 delay-100">
                    <Maximize2 size={20} />
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedImage && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-10">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedImage(null)}
              className="absolute inset-0 bg-gray-900/95 backdrop-blur-xl"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative max-w-6xl w-full aspect-video rounded-lg overflow-hidden shadow-2xl border border-white/10"
            >
              <img
                src={selectedImage}
                alt="Gallery Preview"
                className="w-full h-full object-contain"
                referrerPolicy="no-referrer"
              />
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute lg:top-8 top-2 right-2 lg:right-8 lg:w-14 lg:h-14 w-10 h-10 bg-white/10 hover:bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center text-white transition-all border border-white/10 group"
              >
                <X
                  size={28}
                  className="group-hover:rotate-90 transition-transform duration-300"
                />
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </section>
  );
};
// --- Main App ---

export default function App() {
  const [user, setUser] = useState(null);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  return (
    <div className="min-h-screen bg-white font-sans selection:bg-blue-100 selection:text-[#004a99]">
      <Navbar
        user={user}
        onLoginClick={() => setIsLoginModalOpen(true)}
        onLogout={() => setUser(null)}
      />
      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        onLogin={(userData) => setUser(userData)}
      />
      <main>
        <Hero />
        <Services />
        <Clients />
        <Tracking />
        <WhyUs />
        <PickupForm />
        <CoreValues />
        <Team />
        {/* <Coverage /> */}
        {/* <FAQ /> */}
        {/* <DownloadApp /> */}
        <Gallery />
        {/* <Testimonials /> */}
      </main>
      <Footer />
      {/* <ScrollToTop /> */}
    </div>
  );
}
